import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Empty, Input, Segmented, Select, Space, Spin, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, HolderOutlined, ImportOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { CSS } from '@dnd-kit/utilities';
import { useStore } from '@/stores';
import { SectionCard } from '@/components/SectionCard';
import { getTestPromptsForAgent } from '@/api/agentTestPrompts';
import {
  PromptSendMode,
  getPromptLibrary,
  importPromptLibraryFromTestPrompts,
  reorderPromptLibrary,
  savePromptLibraryRow,
  type AgentLibraryPrompt,
  type PromptLibraryImportResult,
} from '@/api/agentPromptLibrary';
import { PromptLibraryEditorDrawer } from './PromptLibraryEditorDrawer';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// PromptLibraryTab — Agent detail → Prompt library.
//
// Authors what care managers see under "Prompts" in this agent's chat
// (ai.AgentPromptLibrary). Separate from Test prompts, which stay QA-only:
// "Import from test prompts" copies them in once, and the two diverge after.
//
//   • Drag ⋮⋮ to reorder — only while the full list is showing, because a
//     filtered view cannot say where hidden rows belong.
//   • The Active switch hides a prompt from the chat and keeps it here. There
//     is no delete: a deleted imported prompt would return on the next import.
//   • "On click" is the one clinical-safety choice on the page: a prompt that
//     creates something opens in the box so its wording is checked first.
// ─────────────────────────────────────────────────────────────────────────────

type StatusFilter = 'active' | 'off' | 'all';

export const PromptLibraryTab = observer(function PromptLibraryTab() {
  const { agentDetailStore: detail } = useStore();
  const agentId = detail.agent?.id ?? null;

  const [rows, setRows] = useState<AgentLibraryPrompt[]>([]);
  const [sourceCodes, setSourceCodes] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<PromptLibraryImportResult | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('active');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AgentLibraryPrompt | null>(null);

  const load = useCallback(async () => {
    if (agentId == null) return;
    setLoading(true);
    setError(null);
    try {
      // Test prompts only label the Source column ("Test 4.3"); a failure there must not hide the library.
      const [library, tests] = await Promise.all([
        getPromptLibrary(agentId),
        getTestPromptsForAgent(agentId).catch(() => []),
      ]);
      setRows(library);
      setSourceCodes(new Map(tests.filter((t) => t.code).map((t) => [t.id, t.code as string])));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the prompt library');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category).filter((c): c is string => !!c))).sort(),
    [rows],
  );
  const activeCount = rows.filter((r) => r.isActive).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (status === 'all' || (status === 'active' ? r.isActive : !r.isActive)) &&
        (!category || r.category === category) &&
        (!q || r.title.toLowerCase().includes(q) || r.prompt.toLowerCase().includes(q)),
    );
  }, [rows, search, category, status]);

  const canReorder = !search.trim() && !category && status === 'all' && rows.length > 1;

  const sourceLabel = (r: AgentLibraryPrompt) =>
    r.sourceTestPromptId == null ? null : `Test ${sourceCodes.get(r.sourceTestPromptId) ?? `#${r.sourceTestPromptId}`}`;

  const runImport = async () => {
    if (agentId == null) return;
    setImporting(true);
    try {
      const result = await importPromptLibraryFromTestPrompts(agentId);
      setImportResult(result);
      await load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const toggleActive = async (row: AgentLibraryPrompt, isActive: boolean) => {
    setTogglingId(row.id);
    try {
      const saved = await savePromptLibraryRow({ ...row, isActive });
      setRows((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Could not change the prompt');
    } finally {
      setTogglingId(null);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || agentId == null) return;
    const from = rows.findIndex((r) => r.id === active.id);
    const to = rows.findIndex((r) => r.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(rows, from, to);
    setRows(next); // optimistic
    try {
      await reorderPromptLibrary(agentId, next.map((r) => r.id));
      await load(); // pick up the server's renumbering
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Could not save the new order');
      await load();
    }
  };

  const openEditor = (row: AgentLibraryPrompt | null) => {
    setEditing(row);
    setEditorOpen(true);
  };

  const columns: ColumnsType<AgentLibraryPrompt> = [
    {
      key: 'drag',
      width: 36,
      render: () => (canReorder ? <DragHandle /> : null),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      width: 260,
      render: (_, r) => (
        <div>
          <Text strong type={r.isActive ? undefined : 'secondary'}>
            {r.title}
          </Text>
          {r.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>} placement="topLeft">
          <Text type="secondary">{text}</Text>
        </Tooltip>
      ),
    },
    { title: 'Category', dataIndex: 'category', width: 200, render: (c?: string | null) => c ?? <Text type="secondary">—</Text> },
    {
      title: 'On click',
      dataIndex: 'sendMode',
      width: 110,
      render: (m: PromptSendMode) =>
        m === PromptSendMode.Draft ? (
          <Tooltip title="Opens in the input box; the care manager edits, then sends.">
            <Tag color="gold">Edit first</Tag>
          </Tooltip>
        ) : (
          <Tooltip title="Sends straight away as the care manager's own message.">
            <Tag color="blue">Send</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      width: 80,
      render: (_, r) => (
        <Switch
          size="small"
          checked={r.isActive}
          loading={togglingId === r.id}
          onChange={(v) => void toggleActive(r, v)}
          aria-label={r.isActive ? `Hide "${r.title}" from the chat` : `Show "${r.title}" in the chat`}
        />
      ),
    },
    {
      title: 'Source',
      key: 'source',
      width: 100,
      render: (_, r) => {
        const label = sourceLabel(r);
        return label ? (
          <Tag style={{ fontFamily: "'JetBrains Mono', monospace" }}>{label}</Tag>
        ) : (
          <Tag color="green">Studio</Tag>
        );
      },
    },
    {
      key: 'edit',
      width: 48,
      render: (_, r) => (
        <Tooltip title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditor(r)} aria-label={`Edit "${r.title}"`} />
        </Tooltip>
      ),
    },
  ];

  if (agentId == null) return null;

  const table = (
    <Table<AgentLibraryPrompt>
      rowKey="id"
      size="middle"
      columns={columns}
      dataSource={filtered}
      pagination={false}
      components={canReorder ? { body: { row: SortableRow } } : undefined}
      locale={{
        emptyText:
          rows.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No prompts yet. Import this agent's test prompts, or write the first one."
            >
              <Space>
                <Button icon={<ImportOutlined />} loading={importing} onClick={() => void runImport()}>
                  Import from test prompts
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
                  New prompt
                </Button>
              </Space>
            </Empty>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No prompt matches these filters." />
          ),
      }}
    />
  );

  return (
    <SectionCard
      title="Prompt library"
      subtitle="What care managers see under Prompts in this agent's chat. Separate from Test prompts."
      actions={
        <Space>
          <Tooltip title="Reload">
            <Button icon={<ReloadOutlined />} onClick={() => void load()} aria-label="Reload" />
          </Tooltip>
          <Tooltip title="Copies this agent's active test prompts that are not here yet. Never overwrites an edit or switches a prompt back on.">
            <Button icon={<ImportOutlined />} loading={importing} onClick={() => void runImport()}>
              Import from test prompts
            </Button>
          </Tooltip>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
            New prompt
          </Button>
        </Space>
      }
    >
      {importResult && (
        <Alert
          type={importResult.added > 0 ? 'success' : 'info'}
          showIcon
          closable
          onClose={() => setImportResult(null)}
          style={{ marginBottom: 12 }}
          message={
            <span>
              Imported from test prompts: <b>{importResult.added} added</b>, {importResult.alreadyPresent} already here
              {importResult.skippedEmpty > 0 ? `, ${importResult.skippedEmpty} skipped because they were empty` : ''}.
            </span>
          }
          description={
            importResult.added > 0
              ? 'New prompts send on click. Switch any prompt that creates something, such as a task, to Edit first.'
              : undefined
          }
        />
      )}

      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search title or prompt"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          placeholder="All categories"
          value={category}
          onChange={(v) => setCategory(v ?? null)}
          options={categories.map((c) => ({ value: c, label: c }))}
          style={{ width: 240 }}
        />
        <Segmented<StatusFilter>
          value={status}
          onChange={setStatus}
          options={[
            { value: 'active', label: `Active ${activeCount}` },
            { value: 'off', label: `Off ${rows.length - activeCount}` },
            { value: 'all', label: `All ${rows.length}` },
          ]}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {canReorder ? 'Drag ⋮⋮ to reorder.' : rows.length > 1 ? 'Show All with no search or category to reorder.' : null}
        </Text>
      </Space>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      <Spin spinning={loading}>
        {canReorder ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
            <SortableContext items={filtered.map((r) => r.id)} strategy={verticalListSortingStrategy}>
              {table}
            </SortableContext>
          </DndContext>
        ) : (
          table
        )}
      </Spin>

      <PromptLibraryEditorDrawer
        open={editorOpen}
        agentId={agentId}
        row={editing}
        categories={categories}
        sourceLabel={editing ? sourceLabel(editing) : null}
        nextDisplayOrder={rows.length === 0 ? 0 : Math.max(...rows.map((r) => r.displayOrder)) + 10}
        onClose={() => setEditorOpen(false)}
        onSaved={(saved) => {
          setEditorOpen(false);
          setRows((prev) =>
            prev.some((r) => r.id === saved.id) ? prev.map((r) => (r.id === saved.id ? saved : r)) : [...prev, saved],
          );
          void load();
        }}
      />
    </SectionCard>
  );
});

// ── Drag-and-drop rows (antd Table + @dnd-kit, the antd "drag sorting" pattern) ──

const DragHandleContext = createContext<{ setActivatorNodeRef?: (el: HTMLElement | null) => void; listeners?: SyntheticListenerMap }>({});

function DragHandle() {
  const { setActivatorNodeRef, listeners } = useContext(DragHandleContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      aria-label="Drag to reorder"
      {...listeners}
    />
  );
}

function SortableRow(props: HTMLAttributes<HTMLTableRowElement> & { 'data-row-key': number; children?: ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });
  const style: CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9 } : {}),
  };
  const ctx = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);
  return (
    <DragHandleContext.Provider value={ctx}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </DragHandleContext.Provider>
  );
}
